/**
 * Item copies: rarity, item level, affixes and upgrades layered over a
 * catalog item. Everything here is pure and seeded, so the host (drops), the
 * server (heart rewards, rerolls) and the client (upgrade previews) agree.
 *
 * An item copy carries its own rolls in `Equipment.instance` and the catalog
 * numbers they were applied to in `Equipment.baseStats`; `bonuses`, `combat`
 * and `effects` hold the result, so stat code never needs to know about
 * rolls. Numbers are first-pass, to tune.
 */

import type {
    CharacterEquipmentLoadout,
    CharacterEquipmentType,
    Equipment,
    EquipmentBaseStats,
    EquipmentBonuses,
    EquipmentCombatStats,
    ItemAffixRoll,
    ItemInstance,
    ItemRarity,
    ItemSpecialEffects,
} from './types/game';
import type { EssencePool, EssenceType } from './types/essence';
import { getAllEssenceTypes } from './types/essence';
import type { VeilSaturationId } from './types/veil-saturation';
import { ESSENCE_EXCHANGE_RATE } from './essence-exchange';
import {
    AFFIX_COUNT_BY_RARITY,
    clampItemLevel,
    findItemAffix,
    ITEM_LEVEL_MAX,
    listAffixesForEquipmentType,
    resolveAffixValue,
    type ItemAffixDefinition,
} from './item-affixes';
import { rollWeighted, type SeededRng } from './seeded-rng';
import { resolveGearPotency, type CharacterDisciplines } from './gear-requirements';
import type { EquippedItemBehavior } from './types/behaviors';
import {
    findNamedItem,
    getNamedItemCatalog,
    isNamedItemRarity,
    resolveNamedItemAffixes,
    resolveNamedItemLevel,
    type NamedItemDefinition,
} from './named-items';

/** Rarities from most to least common. */
export const ITEM_RARITIES: readonly ItemRarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'];

/** The rarities a drop rolls; the rest are named items. */
export type RolledItemRarity = Exclude<ItemRarity, 'legendary' | 'mythic'>;
export const ROLLED_ITEM_RARITIES: readonly RolledItemRarity[] = ['common', 'uncommon', 'rare', 'epic'];

/**
 * How a rarity reads to players (docs/gameplay/item-rarities.md). Every item
 * in a Crucible is an object the Veil remembered (docs/lore/01-Cosmology.md
 * §7, "What They Contain"), and its affixes are the feelings remembered with
 * it: Hollow (its bare shape, no feeling), Touched (brushed by the Veil, as
 * a veil-touched mortal is), Attuned (in tune with it), Resonant (it pulses
 * with the Soul Stream), Unforgotten (the Veil never lets it go) and
 * Empyreal (a fragment of the Empyreal Veil itself). The ids stay plain for
 * code and storage.
 */
export const ITEM_RARITY_NAMES: Readonly<Record<ItemRarity, string>> = {
    common: 'Hollow',
    uncommon: 'Touched',
    rare: 'Attuned',
    epic: 'Resonant',
    legendary: 'Unforgotten',
    mythic: 'Empyreal',
};

/** Each rarity's color, shared by item cards and dropped-item glows. */
export const ITEM_RARITY_COLORS: Readonly<Record<ItemRarity, string>> = {
    common: '#e8e4dc',
    uncommon: '#5fa8f5',
    rare: '#f2d45c',
    epic: '#b57cf0',
    legendary: '#f0a050',
    mythic: '#3fd6c8',
};

export function resolveItemRarityRank(rarity: ItemRarity): number {
    return ITEM_RARITIES.indexOf(rarity);
}

// ---------------------------------------------------------------------------
// Upgrades
// ---------------------------------------------------------------------------

/** Upgrades run +1 to this. */
export const MAX_ITEM_UPGRADE_LEVEL = 5;
/**
 * Each upgrade raises a weapon's damage by this share of its catalog value,
 * in whole points and at least `ITEM_UPGRADE_MIN_DAMAGE_PER_LEVEL` (see
 * `resolveUpgradeDamagePerLevel`), so every level visibly adds damage.
 */
export const ITEM_UPGRADE_DAMAGE_PER_LEVEL = 0.06;
export const ITEM_UPGRADE_MIN_DAMAGE_PER_LEVEL = 1;
/** Each upgrade adds this much accuracy to a weapon. */
export const ITEM_UPGRADE_ACCURACY_PER_LEVEL = 1;
/** Each upgrade adds this much Resistance to armor. */
export const ITEM_UPGRADE_RESISTANCE_PER_LEVEL = 1;
/** Each upgrade adds this much defense to a shield. */
export const ITEM_UPGRADE_DEFENSE_PER_LEVEL = 1;
/** Each upgrade adds this much essence pool to a ring or amulet. */
export const ITEM_UPGRADE_ESSENCE_POOL_PER_LEVEL = 2;

/** The essence value of the first upgrade; each one after costs `ITEM_UPGRADE_COST_GROWTH` times the last. */
export const ITEM_UPGRADE_BASE_COST_VALUE = 40;
export const ITEM_UPGRADE_COST_GROWTH = 1.75;
/** How much each reroll multiplies the next one's cost by. */
export const ITEM_REROLL_COST_GROWTH = 2;
/** The essence value of the first affix reroll on an item; each one after costs `ITEM_REROLL_COST_GROWTH` times the last. */
export const ITEM_REROLL_BASE_COST_VALUE = 30;
/** Rarer items cost more to upgrade and reroll. Named items are never crafted. */
export const ITEM_RARITY_COST_MULTIPLIER: Readonly<Record<RolledItemRarity, number>> = {
    common: 1,
    uncommon: 1.25,
    rare: 1.6,
    epic: 2,
};
/** The share of an upgrade or reroll paid in the item's own essence; the rest is resonance. */
export const ITEM_COST_AFFINITY_SHARE = 0.6;

/** A plain catalog copy's instance: common, level 1, nothing rolled. */
export function createBaseItemInstance(itemLevel = 1): ItemInstance {
    return { rarity: 'common', itemLevel: clampItemLevel(itemLevel), affixes: [], upgradeLevel: 0, rerollCount: 0 };
}

/** The catalog numbers an item copy was built from. */
export function resolveEquipmentBaseStats(item: Pick<Equipment, 'bonuses' | 'combat' | 'baseStats'>): EquipmentBaseStats {
    return item.baseStats ?? { bonuses: item.bonuses, ...(item.combat ? { combat: item.combat } : {}) };
}

function isWeaponType(equipmentType: CharacterEquipmentType): boolean {
    return equipmentType === 'melee' || equipmentType === 'range';
}

function roundTo(value: number, decimals: number): number {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
}

function addBonus(bonuses: EquipmentBonuses, key: keyof EquipmentBonuses, amount: number): void {
    bonuses[key] = (bonuses[key] ?? 0) + amount;
}

function addCombat(combat: EquipmentCombatStats, key: 'accuracy' | 'defense' | 'speedBonus', amount: number): void {
    combat[key] = (combat[key] ?? 0) + amount;
}

function addEssenceTypeShare(
    record: Partial<Record<EssenceType, number>> | undefined,
    essenceType: EssenceType,
    amount: number,
): Partial<Record<EssenceType, number>> {
    const next = { ...(record ?? {}) };
    next[essenceType] = (next[essenceType] ?? 0) + amount;
    return next;
}

/** Scales every bonus by `potency` (#250). */
function weakenBonuses(bonuses: EquipmentBonuses, potency: number): void {
    for (const key of Object.keys(bonuses) as Array<keyof EquipmentBonuses>) {
        const value = bonuses[key];
        if (typeof value === 'number') {
            bonuses[key] = roundTo(value * potency, 2);
        }
    }
}

/** Scales only what upgrades and affixes added to the combat stats over their base (#250). */
function weakenCombat(combat: EquipmentCombatStats, base: EquipmentCombatStats, potency: number): void {
    for (const key of ['accuracy', 'defense', 'speedBonus'] as const) {
        const value = combat[key];
        if (typeof value === 'number') {
            const baseValue = base[key] ?? 0;
            combat[key] = roundTo(baseValue + (value - baseValue) * potency, 2);
        }
    }
}

/** Scales an item's special effects by `potency` (#250). */
function weakenEffects(effects: ItemSpecialEffects, potency: number): ItemSpecialEffects {
    const scaleShares = (record: Partial<Record<EssenceType, number>> | undefined): Partial<Record<EssenceType, number>> | undefined => record
        && Object.fromEntries(Object.entries(record).map(([essenceType, value]) => [essenceType, roundTo((value ?? 0) * potency, 3)]));
    return {
        ...effects,
        ...(effects.abilityCostReduction ? { abilityCostReduction: scaleShares(effects.abilityCostReduction) } : {}),
        ...(effects.abilityCooldownReduction ? { abilityCooldownReduction: scaleShares(effects.abilityCooldownReduction) } : {}),
        ...(effects.crystalChannelReduction !== undefined ? { crystalChannelReduction: roundTo(effects.crystalChannelReduction * potency, 3) } : {}),
        ...(effects.drawRestoreBonus !== undefined ? { drawRestoreBonus: effects.drawRestoreBonus * potency } : {}),
        ...(effects.praxisGain !== undefined ? { praxisGain: roundTo(effects.praxisGain * potency, 3) } : {}),
    };
}

/** Adds one affix roll's effect onto stats being built. Returns the updated effects. */
function applyAffix(
    affix: ItemAffixDefinition,
    value: number,
    bonuses: EquipmentBonuses,
    combat: EquipmentCombatStats,
    effects: ItemSpecialEffects,
    damageShare: { value: number },
): ItemSpecialEffects {
    const target = affix.target;
    switch (target.kind) {
        case 'bonus':
            addBonus(bonuses, target.key, value);
            return effects;
        case 'combat':
            addCombat(combat, target.key, value);
            return effects;
        case 'damage-percent':
            damageShare.value += value;
            return effects;
        case 'ability-cost':
            return { ...effects, abilityCostReduction: addEssenceTypeShare(effects.abilityCostReduction, target.essenceType, value) };
        case 'ability-cooldown':
            return { ...effects, abilityCooldownReduction: addEssenceTypeShare(effects.abilityCooldownReduction, target.essenceType, value) };
        case 'crystal-channel':
            return { ...effects, crystalChannelReduction: (effects.crystalChannelReduction ?? 0) + value };
        case 'draw-restore':
            return { ...effects, drawRestoreBonus: (effects.drawRestoreBonus ?? 0) + value };
        case 'praxis-gain':
            return { ...effects, praxisGain: (effects.praxisGain ?? 0) + value };
    }
}

/**
 * Applies an item copy's rolls to its catalog numbers: its upgrade level,
 * then each affix. Always starts from `baseStats` (or the item's own numbers
 * when it has none yet), so it is safe to call again after an upgrade or
 * reroll. Unknown affix ids (retired affixes) are kept but do nothing.
 *
 * A named copy takes its definition's name and fixed affixes (so a
 * rebalanced definition reaches every copy) and is never upgraded. A copy
 * whose named item no longer exists keeps its stored affixes.
 *
 * `potency` (below 1) weakens it for a wearer short of its requirement
 * (#250, `resolveGearPotency`): its bonuses, and what its upgrades and
 * affixes add, scale by it; its base combat stats (damage, defense, range,
 * cooldown) don't. Starting from the base, it can always be materialized
 * again at another potency.
 */
export function buildEquipment(
    item: Equipment,
    stored: ItemInstance,
    namedCatalog: readonly NamedItemDefinition[] = getNamedItemCatalog(),
    potency = 1,
): Equipment {
    const base = resolveEquipmentBaseStats(item);
    const baseName = base.baseName ?? item.name;
    const named = findNamedItem(stored.namedItemId, namedCatalog);
    const instance: ItemInstance = named
        ? {
            ...stored,
            rarity: named.rarity,
            itemLevel: resolveNamedItemLevel(named, stored.itemLevel),
            affixes: resolveNamedItemAffixes(named, stored.itemLevel),
            upgradeLevel: 0,
        }
        : stored;
    const bonuses: EquipmentBonuses = { ...base.bonuses };
    const combat: EquipmentCombatStats = { ...(base.combat ?? {}) };
    let effects: ItemSpecialEffects = {};
    const upgradeLevel = Math.min(MAX_ITEM_UPGRADE_LEVEL, Math.max(0, Math.round(instance.upgradeLevel)));
    const damageShare = { value: 0 };

    // Whole points a weapon's upgrades add to its damage, before any damage affix scales it.
    let upgradeDamage = 0;
    if (isWeaponType(item.equipmentType)) {
        upgradeDamage = upgradeLevel * resolveUpgradeDamagePerLevel(base.combat?.baseDamage ?? 0);
        if (upgradeLevel > 0) {
            addCombat(combat, 'accuracy', upgradeLevel * ITEM_UPGRADE_ACCURACY_PER_LEVEL);
        }
    } else if (upgradeLevel > 0 && item.equipmentType === 'armor') {
        addBonus(bonuses, 'resistance', upgradeLevel * ITEM_UPGRADE_RESISTANCE_PER_LEVEL);
    } else if (upgradeLevel > 0 && item.equipmentType === 'shield') {
        addCombat(combat, 'defense', upgradeLevel * ITEM_UPGRADE_DEFENSE_PER_LEVEL);
    } else if (upgradeLevel > 0 && (item.equipmentType === 'ring' || item.equipmentType === 'amulet')) {
        addBonus(bonuses, 'essencePool', upgradeLevel * ITEM_UPGRADE_ESSENCE_POOL_PER_LEVEL);
    }

    for (const roll of instance.affixes) {
        const affix = findItemAffix(roll.affixId);
        if (affix) {
            effects = applyAffix(affix, roll.value, bonuses, combat, effects, damageShare);
        }
    }

    const weakened = potency < 1;
    if (weakened) {
        weakenBonuses(bonuses, potency);
        weakenCombat(combat, base.combat ?? {}, potency);
        damageShare.value *= potency;
        upgradeDamage *= potency;
        effects = weakenEffects(effects, potency);
    }
    if ((damageShare.value !== 0 || upgradeDamage !== 0) && typeof combat.baseDamage === 'number') {
        combat.baseDamage = roundTo((combat.baseDamage + upgradeDamage) * (1 + damageShare.value), 2);
    }
    if (effects.drawRestoreBonus !== undefined) {
        effects.drawRestoreBonus = roundTo(effects.drawRestoreBonus, 3);
    }

    const { effects: _previousEffects, potency: _previousPotency, ...rest } = item;
    void _previousEffects;
    void _previousPotency;
    return {
        ...rest,
        name: named?.name ?? baseName,
        baseStats: {
            bonuses: { ...base.bonuses },
            ...(base.combat ? { combat: { ...base.combat } } : {}),
            ...(named ? { baseName } : {}),
        },
        bonuses,
        ...(Object.keys(combat).length > 0 ? { combat } : {}),
        ...(Object.keys(effects).length > 0 ? { effects } : {}),
        ...(weakened ? { potency } : {}),
        instance: {
            rarity: instance.rarity,
            itemLevel: clampItemLevel(instance.itemLevel),
            affixes: instance.affixes.map((roll) => ({ ...roll })),
            upgradeLevel,
            rerollCount: Math.max(0, Math.round(instance.rerollCount)),
            ...(instance.namedItemId !== undefined ? { namedItemId: instance.namedItemId } : {}),
        },
    };
}

/** The whole points of damage each upgrade level adds to a weapon of this catalog damage. */
export function resolveUpgradeDamagePerLevel(baseDamage: number): number {
    return Math.max(ITEM_UPGRADE_MIN_DAMAGE_PER_LEVEL, Math.round(baseDamage * ITEM_UPGRADE_DAMAGE_PER_LEVEL));
}

/** A stat that upgrade levels raise (see `buildEquipment`). */
export type UpgradeStat = 'damage' | 'accuracy' | 'resistance' | 'defense' | 'essencePool';

/**
 * The stats upgrade levels raise on this kind of equipment, as this copy has
 * them: what an upgrade path compares level to level. Matches the upgrade
 * branch of `buildEquipment`; damage includes affixes' share.
 */
export function resolveUpgradeStats(item: Equipment): { readonly stat: UpgradeStat; readonly value: number }[] {
    if (isWeaponType(item.equipmentType)) {
        return [
            { stat: 'damage', value: item.combat?.baseDamage ?? 0 },
            { stat: 'accuracy', value: item.combat?.accuracy ?? 0 },
        ];
    }
    if (item.equipmentType === 'armor') {
        return [{ stat: 'resistance', value: item.bonuses.resistance ?? 0 }];
    }
    if (item.equipmentType === 'shield') {
        return [{ stat: 'defense', value: item.combat?.defense ?? 0 }];
    }
    if (item.equipmentType === 'ring' || item.equipmentType === 'amulet') {
        return [{ stat: 'essencePool', value: item.bonuses.essencePool ?? 0 }];
    }
    return [];
}

/** Whether an item copy is a named item (Unforgotten or Empyreal). */
export function isNamedItem(item: Pick<Equipment, 'instance'>): boolean {
    return item.instance !== undefined && isNamedItemRarity(item.instance.rarity);
}

// ---------------------------------------------------------------------------
// Rolling
// ---------------------------------------------------------------------------

/** What a drop's rolls depend on. */
export interface ItemRollContext {
    readonly itemLevel: number;
    /** The Crucible's dominant essence types, strongest first: affixes leaning to them roll more often. */
    readonly essenceMix?: readonly EssenceType[];
    /** The drop is at least this rare (a guardian, a treasure cache). */
    readonly minRarity?: RolledItemRarity;
    /** Multiplies the weight of every rarity above common (elites, caches). */
    readonly rarityBoost?: number;
    /**
     * The drop is at most this rare: the Crucible's Veil Saturation cap
     * (`maxItemRarity`). Wins over `minRarity`. Below 'legendary', no item
     * turns Unforgotten.
     */
    readonly maxRarity?: Exclude<ItemRarity, 'mythic'>;
}

/** Rarity weights at an item level: deeper finds lean rarer. Named items are never rolled here. */
export function resolveRarityWeights(itemLevel: number, rarityBoost = 1): Record<RolledItemRarity, number> {
    const level = clampItemLevel(itemLevel);
    const boost = Math.max(0, rarityBoost);
    return {
        common: Math.max(10, 60 - 3 * level),
        uncommon: (28 + 1.5 * level) * boost,
        rare: (9 + 1.1 * level) * boost,
        epic: (2 + 0.4 * level) * boost,
    };
}

function pickWeighted<T>(entries: ReadonlyArray<{ readonly value: T; readonly weight: number }>, rng: SeededRng): T | undefined {
    return rollWeighted(entries, (entry) => entry.weight, rng)?.value;
}

export function rollItemRarity(context: ItemRollContext, rng: SeededRng): RolledItemRarity {
    const weights = resolveRarityWeights(context.itemLevel, context.rarityBoost ?? 1);
    const maxRank = context.maxRarity ? resolveItemRarityRank(context.maxRarity) : Infinity;
    const minRank = Math.min(context.minRarity ? resolveItemRarityRank(context.minRarity) : 0, maxRank);
    const rolled = pickWeighted(
        ROLLED_ITEM_RARITIES.map((rarity) => {
            const rank = resolveItemRarityRank(rarity);
            return { value: rarity, weight: rank >= minRank && rank <= maxRank ? weights[rarity] : 0 };
        }),
        rng,
    );
    return rolled ?? ROLLED_ITEM_RARITIES[minRank] ?? 'common';
}

/** How much more often an affix leaning to a Crucible's dominant essence rolls (strongest type gets the most). */
const ESSENCE_MIX_AFFIX_WEIGHT = [1.5, 0.75, 0.4] as const;

function resolveAffixWeight(affix: ItemAffixDefinition, essenceMix: readonly EssenceType[] | undefined): number {
    const rank = essenceMix?.indexOf(affix.essence) ?? -1;
    const lean = rank >= 0 ? ESSENCE_MIX_AFFIX_WEIGHT[rank] ?? 0 : 0;
    return affix.weight * (1 + lean);
}

/**
 * Rolls one affix an item of `equipmentType` can take, skipping the groups
 * already on it (and `excludeAffixId`, so a reroll always changes something
 * when it can). Resonant items roll in the upper half of each range.
 */
/**
 * The affixes a roll for this kind of equipment can land on: none from a
 * group another affix already holds, and not `excludeAffixId` (the one a
 * reroll replaces).
 */
function listAffixCandidates(
    equipmentType: CharacterEquipmentType,
    takenGroups: ReadonlySet<string>,
    excludeAffixId?: string,
): ItemAffixDefinition[] {
    return listAffixesForEquipmentType(equipmentType)
        .filter((affix) => !takenGroups.has(affix.group) && affix.id !== excludeAffixId);
}

export function rollItemAffix(
    equipmentType: CharacterEquipmentType,
    rarity: ItemRarity,
    context: ItemRollContext,
    rng: SeededRng,
    takenGroups: ReadonlySet<string>,
    excludeAffixId?: string,
): ItemAffixRoll | undefined {
    const candidates = listAffixCandidates(equipmentType, takenGroups, excludeAffixId);
    const affix = pickWeighted(
        candidates.map((candidate) => ({ value: candidate, weight: resolveAffixWeight(candidate, context.essenceMix) })),
        rng,
    );
    if (!affix) {
        return undefined;
    }
    const roll = rarity === 'epic' ? 0.5 + rng.random() * 0.5 : rng.random();
    return { affixId: affix.id, value: resolveAffixValue(affix, context.itemLevel, roll) };
}

/** Rolls a new copy's rarity and affixes. */
export function rollItemInstance(
    equipmentType: CharacterEquipmentType,
    context: ItemRollContext,
    rng: SeededRng,
): ItemInstance {
    const rarity = rollItemRarity(context, rng);
    const affixes: ItemAffixRoll[] = [];
    const takenGroups = new Set<string>();
    for (let index = 0; index < AFFIX_COUNT_BY_RARITY[rarity]; index += 1) {
        const roll = rollItemAffix(equipmentType, rarity, context, rng, takenGroups);
        if (!roll) {
            break;
        }
        affixes.push(roll);
        const group = findItemAffix(roll.affixId)?.group;
        if (group) {
            takenGroups.add(group);
        }
    }
    return { rarity, itemLevel: clampItemLevel(context.itemLevel), affixes, upgradeLevel: 0, rerollCount: 0 };
}

/** Whether a catalog item can drop at an item level: not below its `minDropLevel` (#258). */
export function canDropAtItemLevel(item: Pick<Equipment, 'minDropLevel'>, itemLevel: number): boolean {
    return (item.minDropLevel ?? 1) <= clampItemLevel(itemLevel);
}

/** The chance a drop leans toward a copy its finder wields at full strength (#258). */
export const USABLE_DROP_BIAS = 0.6;
/** How many copies a leaning drop rolls, at most, looking for one its finder wields at full strength. */
export const USABLE_DROP_ATTEMPTS = 8;

/** What a drop's rolls depend on, plus who it drops for. */
export interface LootRollContext extends ItemRollContext {
    /** The finder's disciplines: most drops lean toward a copy they wield at full strength. Absent: unbiased. */
    readonly finderDisciplines?: CharacterDisciplines;
}

function rollLootCopy(pool: readonly Equipment[], context: ItemRollContext, rng: SeededRng, id: string): Equipment | undefined {
    const base = pool[rng.randomInt(0, pool.length)];
    if (!base) {
        return undefined;
    }
    const instance = rollItemInstance(base.equipmentType, context, rng);
    return buildEquipment({ ...withoutItemInstance(base), id }, instance);
}

/**
 * Rolls one dropped item: picks a catalog item allowed at the item level
 * (its `minDropLevel`; the whole catalog when none is), then rolls its copy
 * at the item level. `id` is the new copy's game item id. Returns undefined for
 * an empty catalog.
 *
 * With `finderDisciplines`, a drop leans (`USABLE_DROP_BIAS` of the time)
 * toward a copy its finder wields at full strength (`resolveGearPotency`
 * 1): it rerolls the whole copy, base and rarity, up to
 * `USABLE_DROP_ATTEMPTS` times until one is, keeping the last otherwise.
 * Rerolling the whole copy rather than filtering bases is what works with
 * #250's requirement, which comes from a copy's rarity and item level as
 * much as its type: no base is wieldable or not before it is rolled. All
 * rolls draw from `rng`, and the lean draws nothing without a finder, so
 * unbiased drops roll exactly as before.
 */
export function rollLootItem(
    catalog: readonly Equipment[],
    context: LootRollContext,
    rng: SeededRng,
    id: string,
): Equipment | undefined {
    const itemLevel = clampItemLevel(context.itemLevel);
    const allowed = catalog.filter((item) => canDropAtItemLevel(item, itemLevel));
    const pool = allowed.length > 0 ? allowed : catalog;
    if (pool.length === 0) {
        return undefined;
    }

    const { finderDisciplines, ...rollContext } = context;
    const copyContext: ItemRollContext = { ...rollContext, itemLevel };
    const leaning = finderDisciplines !== undefined && rng.random() < USABLE_DROP_BIAS;
    let copy = rollLootCopy(pool, copyContext, rng, id);
    if (!leaning || !finderDisciplines) {
        return copy;
    }
    for (let attempt = 1; copy && attempt < USABLE_DROP_ATTEMPTS; attempt += 1) {
        if (resolveGearPotency(copy, finderDisciplines) === 1) {
            break;
        }
        copy = rollLootCopy(pool, copyContext, rng, id);
    }
    return copy;
}

/** A catalog copy of an item: its rolls and their effects removed. */
export function withoutItemInstance(item: Equipment): Equipment {
    const base = resolveEquipmentBaseStats(item);
    const { instance: _instance, baseStats: _baseStats, effects: _effects, combat: _combat, ...rest } = item;
    void _instance;
    void _baseStats;
    void _effects;
    void _combat;
    return {
        ...rest,
        name: base.baseName ?? item.name,
        bonuses: { ...base.bonuses },
        ...(base.combat ? { combat: { ...base.combat } } : {}),
    };
}

// ---------------------------------------------------------------------------
// Named items
// ---------------------------------------------------------------------------

/** The heart's last echo: each item's chance to be Unforgotten, at item level 1 and at the cap. */
export const UNFORGOTTEN_HEART_CHANCE = { atLevel1: 0.04, atMaxLevel: 0.08 } as const;
/** A guardian's guaranteed item: its chance to be Unforgotten, at item level 1 and at the cap. */
export const UNFORGOTTEN_GUARDIAN_CHANCE = { atLevel1: 0.01, atMaxLevel: 0.03 } as const;

/** Where an Unforgotten item can come from. Empyreal items never drop. */
export type UnforgottenSource = 'heart' | 'guardian';

/** The chance one item from `source` is Unforgotten instead of rolled. */
export function resolveUnforgottenChance(source: UnforgottenSource, itemLevel: number): number {
    const range = source === 'heart' ? UNFORGOTTEN_HEART_CHANCE : UNFORGOTTEN_GUARDIAN_CHANCE;
    const t = (clampItemLevel(itemLevel) - 1) / (ITEM_LEVEL_MAX - 1);
    return range.atLevel1 + (range.atMaxLevel - range.atLevel1) * t;
}

/** A named item copy built on its catalog base. `id` is the new copy's game item id. */
export function createNamedItem(
    base: Equipment,
    definition: NamedItemDefinition,
    itemLevel: number,
    id: string,
    namedCatalog: readonly NamedItemDefinition[] = getNamedItemCatalog(),
): Equipment {
    const instance: ItemInstance = {
        rarity: definition.rarity,
        itemLevel: resolveNamedItemLevel(definition, itemLevel),
        affixes: [],
        upgradeLevel: 0,
        rerollCount: 0,
        namedItemId: definition.id,
    };
    return buildEquipment({ ...withoutItemInstance(base), id }, instance, namedCatalog);
}

/** How much more often a named item leaning to a Crucible's dominant essence drops (strongest type gets the most). */
const ESSENCE_MIX_NAMED_WEIGHT = [2, 1, 0.5] as const;

/**
 * Rolls an Unforgotten item: one whose base is in `catalog` and that can drop
 * at the item level (its own minimum and its base's `minDropLevel`),
 * leaning to the Crucible's essence. Undefined when none can.
 */
export function rollUnforgottenItem(
    catalog: readonly Equipment[],
    context: Pick<ItemRollContext, 'itemLevel' | 'essenceMix'>,
    rng: SeededRng,
    id: string,
    namedCatalog: readonly NamedItemDefinition[] = getNamedItemCatalog(),
): Equipment | undefined {
    const itemLevel = clampItemLevel(context.itemLevel);
    const bases = new Map(catalog.map((item) => [item.itemDefinitionId, item]));
    const definition = pickWeighted(
        namedCatalog
            .filter((named) => named.rarity === 'legendary'
                && bases.has(named.baseItemId)
                && canDropAtItemLevel(bases.get(named.baseItemId)!, itemLevel)
                && (named.minItemLevel ?? 1) <= itemLevel)
            .map((named) => {
                const rank = context.essenceMix?.indexOf(named.essence) ?? -1;
                return { value: named, weight: 1 + (rank >= 0 ? ESSENCE_MIX_NAMED_WEIGHT[rank] ?? 0 : 0) };
            }),
        rng,
    );
    const base = definition ? bases.get(definition.baseItemId) : undefined;
    return definition && base ? createNamedItem(base, definition, itemLevel, id, namedCatalog) : undefined;
}

/**
 * One item from a source that can give an Unforgotten (the heart's echo, a
 * guardian): Unforgotten at `resolveUnforgottenChance`, else rolled as usual.
 * Never Unforgotten when `maxRarity` is below 'legendary'. The Unforgotten
 * roll is always drawn, so the rest of the drop's rolls don't shift when a
 * named item is added or capped away.
 */
export function rollLootItemWithUnforgotten(
    catalog: readonly Equipment[],
    context: LootRollContext,
    source: UnforgottenSource,
    rng: SeededRng,
    id: string,
    namedCatalog: readonly NamedItemDefinition[] = getNamedItemCatalog(),
): Equipment | undefined {
    const allowed = !context.maxRarity || resolveItemRarityRank(context.maxRarity) >= resolveItemRarityRank('legendary');
    const unforgotten = rng.random() < resolveUnforgottenChance(source, context.itemLevel);
    if (unforgotten && allowed) {
        const named = rollUnforgottenItem(catalog, context, rng, id, namedCatalog);
        if (named) {
            return named;
        }
    }
    return rollLootItem(catalog, context, rng, id);
}

/** Why an item can't be equipped alongside the rest of a loadout. */
export type NamedItemEquipConflict =
    /** Only one Empyreal item can be worn. */
    | 'second-empyreal'
    /** Two copies of the same named item can't be worn together. */
    | 'duplicate-named';

/**
 * Whether `item` can be worn alongside `others`: the equipped items that
 * stay on after the move (leave out whatever it replaces). Undefined when it can.
 */
export function findNamedItemEquipConflict(
    item: Pick<Equipment, 'id' | 'instance'>,
    others: Iterable<Pick<Equipment, 'id' | 'instance'> | null | undefined>,
): NamedItemEquipConflict | undefined {
    const instance = item.instance;
    if (!instance || !isNamedItemRarity(instance.rarity)) {
        return undefined;
    }
    for (const other of others) {
        if (!other || other.id === item.id || !other.instance) {
            continue;
        }
        if (instance.rarity === 'mythic' && other.instance.rarity === 'mythic') {
            return 'second-empyreal';
        }
        if (instance.namedItemId !== undefined && other.instance.namedItemId === instance.namedItemId) {
            return 'duplicate-named';
        }
    }
    return undefined;
}

/** The first named item rule a whole set of equipped items breaks, or undefined. */
export function findLoadoutNamedItemConflict(
    equipped: readonly (Pick<Equipment, 'id' | 'instance'> | null | undefined)[],
): NamedItemEquipConflict | undefined {
    for (const item of equipped) {
        const conflict = item ? findNamedItemEquipConflict(item, equipped) : undefined;
        if (conflict) {
            return conflict;
        }
    }
    return undefined;
}

/** What a player reads when a named item can't be equipped. */
export const NAMED_ITEM_EQUIP_CONFLICT_MESSAGES: Readonly<Record<NamedItemEquipConflict, string>> = {
    'second-empyreal': 'Only one Empyreal item can be worn at a time.',
    'duplicate-named': 'You are already wearing this item.',
};

/** Every equipped named item's unique behaviors, with the copy each comes from. */
export function resolveEquippedItemBehaviors(
    equipped: CharacterEquipmentLoadout | undefined,
    namedCatalog: readonly NamedItemDefinition[] = getNamedItemCatalog(),
): EquippedItemBehavior[] {
    return equippedItems(equipped).flatMap((item) => {
        const named = findNamedItem(item.instance?.namedItemId, namedCatalog);
        return named ? named.behaviors.map((behavior, behaviorIndex) => ({ itemId: item.id, namedItemId: named.id, behavior, behaviorIndex })) : [];
    });
}

// ---------------------------------------------------------------------------
// Item level
// ---------------------------------------------------------------------------

/** How many item levels each Veil Saturation band adds. */
export const ITEM_LEVEL_SATURATION_BONUS: Readonly<Record<VeilSaturationId, number>> = {
    faint: 0,
    stirring: 1,
    charged: 2,
    overflowing: 3,
};

/**
 * The item level of a find: one plus the room's depth (map area difficulty,
 * 0 at the start), raised by the Crucible's Veil Saturation.
 */
export function resolveItemLevel(areaDifficulty: number, veilSaturation: VeilSaturationId): number {
    const depth = Number.isFinite(areaDifficulty) ? Math.max(0, areaDifficulty) : 0;
    return clampItemLevel(1 + depth + ITEM_LEVEL_SATURATION_BONUS[veilSaturation]);
}

/** The item level of the heart's last echo: the deepest point, so the top of the Crucible's range. */
export function resolveHeartItemLevel(maxAreaDifficulty: number, veilSaturation: VeilSaturationId): number {
    return Math.min(ITEM_LEVEL_MAX, resolveItemLevel(maxAreaDifficulty, veilSaturation) + 1);
}

// ---------------------------------------------------------------------------
// Effects
// ---------------------------------------------------------------------------

function equippedItems(equipped: CharacterEquipmentLoadout | undefined): Equipment[] {
    if (!equipped) {
        return [];
    }
    return [equipped.weapons[0], equipped.weapons[1], equipped.armor, ...equipped.miscellaneous]
        .filter((item): item is Equipment => item !== undefined && item !== null && item.itemDefinitionId.length > 0);
}

/** Every equipped item's special effects, summed. */
export function resolveEquippedItemEffects(equipped: CharacterEquipmentLoadout | undefined): ItemSpecialEffects {
    let total: ItemSpecialEffects = {};
    for (const item of equippedItems(equipped)) {
        const effects = item.effects;
        if (!effects) {
            continue;
        }
        for (const essenceType of getAllEssenceTypes()) {
            const cost = effects.abilityCostReduction?.[essenceType];
            if (cost) {
                total = { ...total, abilityCostReduction: addEssenceTypeShare(total.abilityCostReduction, essenceType, cost) };
            }
            const cooldown = effects.abilityCooldownReduction?.[essenceType];
            if (cooldown) {
                total = { ...total, abilityCooldownReduction: addEssenceTypeShare(total.abilityCooldownReduction, essenceType, cooldown) };
            }
        }
        if (effects.crystalChannelReduction) {
            total = { ...total, crystalChannelReduction: (total.crystalChannelReduction ?? 0) + effects.crystalChannelReduction };
        }
        if (effects.drawRestoreBonus) {
            total = { ...total, drawRestoreBonus: (total.drawRestoreBonus ?? 0) + effects.drawRestoreBonus };
        }
        if (effects.praxisGain) {
            total = { ...total, praxisGain: (total.praxisGain ?? 0) + effects.praxisGain };
        }
    }
    return total;
}

/** Item-granted reductions stop here, so stacking them can never make something free. */
export const MAX_ITEM_REDUCTION = 0.5;

/** The share off one essence type's ability costs from equipped items, capped. */
export function resolveItemAbilityCostReduction(equipped: CharacterEquipmentLoadout | undefined, essenceType: EssenceType): number {
    return Math.min(MAX_ITEM_REDUCTION, resolveEquippedItemEffects(equipped).abilityCostReduction?.[essenceType] ?? 0);
}

/** The share off one essence type's ability cooldowns from equipped items, capped. */
export function resolveItemAbilityCooldownReduction(equipped: CharacterEquipmentLoadout | undefined, essenceType: EssenceType): number {
    return Math.min(MAX_ITEM_REDUCTION, resolveEquippedItemEffects(equipped).abilityCooldownReduction?.[essenceType] ?? 0);
}

/** The share off crystal channel time from equipped items, capped. */
export function resolveItemCrystalChannelReduction(equipped: CharacterEquipmentLoadout | undefined): number {
    return Math.min(MAX_ITEM_REDUCTION, resolveEquippedItemEffects(equipped).crystalChannelReduction ?? 0);
}

// ---------------------------------------------------------------------------
// Costs
// ---------------------------------------------------------------------------

/**
 * The essence an item is "made of": the type most of its affixes lean to,
 * else the strongest type in its catalog trade value, else resonance.
 * Upgrades and rerolls are paid partly in it.
 */
export function resolveItemEssenceAffinity(item: Pick<Equipment, 'instance' | 'essenceValue'>): EssenceType {
    const counts = new Map<EssenceType, number>();
    for (const roll of item.instance?.affixes ?? []) {
        const essence = findItemAffix(roll.affixId)?.essence;
        if (essence) {
            counts.set(essence, (counts.get(essence) ?? 0) + 1);
        }
    }
    let best: EssenceType | undefined;
    for (const type of getAllEssenceTypes()) {
        if ((counts.get(type) ?? 0) > (best ? counts.get(best) ?? 0 : 0)) {
            best = type;
        }
    }
    if (best) {
        return best;
    }

    let strongest: EssenceType | undefined;
    for (const type of getAllEssenceTypes()) {
        if ((item.essenceValue[type] ?? 0) > (strongest ? item.essenceValue[strongest] ?? 0 : 0)) {
            strongest = type;
        }
    }
    return strongest ?? 'resonance';
}

/**
 * Splits an essence value into a typed price: `ITEM_COST_AFFINITY_SHARE` of
 * it in the item's own essence (converted at exchange rates, at least one),
 * the rest in resonance.
 */
export function resolveItemCostSplit(value: number, affinity: EssenceType): Partial<EssencePool> {
    const total = Math.max(1, Math.round(value));
    if (affinity === 'resonance') {
        return { resonance: total };
    }
    const affinityValue = total * ITEM_COST_AFFINITY_SHARE;
    const affinityUnits = Math.max(1, Math.round(affinityValue / ESSENCE_EXCHANGE_RATE[affinity]));
    const resonanceUnits = Math.max(0, Math.round(total - affinityValue));
    return resonanceUnits > 0 ? { [affinity]: affinityUnits, resonance: resonanceUnits } : { [affinity]: affinityUnits };
}

/** A rolled item's rarity, or null for a named item, which is never crafted. */
function craftableRarityOf(item: Pick<Equipment, 'instance'>): RolledItemRarity | null {
    const rarity = item.instance?.rarity ?? 'common';
    return isNamedItemRarity(rarity) ? null : rarity;
}

/** What the next upgrade costs, or undefined when the item is fully upgraded or a named item. */
export function resolveItemUpgradeCost(item: Pick<Equipment, 'instance' | 'essenceValue'>): Partial<EssencePool> | undefined {
    const level = item.instance?.upgradeLevel ?? 0;
    const rarity = craftableRarityOf(item);
    if (level >= MAX_ITEM_UPGRADE_LEVEL || rarity === null) {
        return undefined;
    }
    const value = ITEM_UPGRADE_BASE_COST_VALUE * ITEM_UPGRADE_COST_GROWTH ** level * ITEM_RARITY_COST_MULTIPLIER[rarity];
    return resolveItemCostSplit(value, resolveItemEssenceAffinity(item));
}

/** What the next affix reroll costs, or undefined when the item has no affixes or is a named item. */
export function resolveItemRerollCost(item: Pick<Equipment, 'instance' | 'essenceValue'>): Partial<EssencePool> | undefined {
    const rarity = craftableRarityOf(item);
    if ((item.instance?.affixes.length ?? 0) === 0 || rarity === null) {
        return undefined;
    }
    const value = ITEM_REROLL_BASE_COST_VALUE * ITEM_REROLL_COST_GROWTH ** (item.instance?.rerollCount ?? 0) * ITEM_RARITY_COST_MULTIPLIER[rarity];
    return resolveItemCostSplit(value, resolveItemEssenceAffinity(item));
}

/** The item with one more upgrade, or undefined when it is fully upgraded or a named item. */
export function withEquipmentUpgrade(item: Equipment): Equipment | undefined {
    const instance = item.instance ?? createBaseItemInstance();
    if (instance.upgradeLevel >= MAX_ITEM_UPGRADE_LEVEL || isNamedItemRarity(instance.rarity)) {
        return undefined;
    }
    return buildEquipment(item, { ...instance, upgradeLevel: instance.upgradeLevel + 1 });
}

/** The affix groups held by every affix of an item but the one at `affixIndex`. */
function resolveOtherAffixGroups(instance: ItemInstance, affixIndex: number): Set<string> {
    return new Set(
        instance.affixes
            .filter((_, index) => index !== affixIndex)
            .map((roll) => findItemAffix(roll.affixId)?.group)
            .filter((group): group is string => group !== undefined),
    );
}

/**
 * What rerolling the affix at `affixIndex` can turn it into: the affixes
 * `withEquipmentAffixRerolled` draws from. Empty when it can't be rerolled.
 */
export function listRerollCandidates(item: Equipment, affixIndex: number): ItemAffixDefinition[] {
    const instance = item.instance;
    const current = instance?.affixes[affixIndex];
    if (!instance || !current || isNamedItemRarity(instance.rarity)) {
        return [];
    }
    return listAffixCandidates(item.equipmentType, resolveOtherAffixGroups(instance, affixIndex), current.affixId);
}

/**
 * The item with the affix at `affixIndex` rolled again: a different affix
 * the item can take, at the item's level and rarity. Undefined when there is no
 * such affix or nothing else it could roll, or for a named item.
 */
export function withEquipmentAffixRerolled(item: Equipment, affixIndex: number, rng: SeededRng): Equipment | undefined {
    const instance = item.instance;
    const current = instance?.affixes[affixIndex];
    if (!instance || !current || isNamedItemRarity(instance.rarity)) {
        return undefined;
    }
    const takenGroups = resolveOtherAffixGroups(instance, affixIndex);
    const replacement = rollItemAffix(
        item.equipmentType,
        instance.rarity,
        { itemLevel: instance.itemLevel },
        rng,
        takenGroups,
        current.affixId,
    );
    if (!replacement) {
        return undefined;
    }
    const affixes = instance.affixes.map((roll, index) => (index === affixIndex ? replacement : roll));
    return buildEquipment(item, { ...instance, affixes, rerollCount: instance.rerollCount + 1 });
}
